import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from '../RootStore';

interface StorageEstimate {
    usage: number;
    quota: number;
    usageDetails?: {
        indexedDB?: number;
        cacheStorage?: number;
        serviceWorkerRegistrations?: number;
    };
}

export class StorageStore {
    rootStore: RootStore;
    estimate: StorageEstimate | null = null;
    isPersisted = false;
    canPersist = false;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;

        this.checkStorageStatus();
    }

    async checkStorageStatus() {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            try {
                const estimate = await navigator.storage.estimate();
                runInAction(() => {
                    this.estimate = estimate as StorageEstimate;
                });
            } catch (error) {
                console.error('Failed to estimate storage:', error);
            }
        }

        if ('storage' in navigator && 'persisted' in navigator.storage) {
            try {
                const persisted = await navigator.storage.persisted();
                runInAction(() => {
                    this.isPersisted = persisted;
                });
            } catch (error) {
                console.error('Failed to check persistence:', error);
            }
        }

        if ('storage' in navigator && 'persist' in navigator.storage) {
            runInAction(() => {
                this.canPersist = true;
            });
        }
    }

    async requestPersistence() {
        if (!this.canPersist) {
            throw new Error('Storage persistence not supported');
        }

        try {
            const granted = await navigator.storage.persist();
            runInAction(() => {
                this.isPersisted = granted;
            });

            if (granted && this.rootStore.notificationStore) {
                this.rootStore.notificationStore.addNotification({
                    message: 'Storage persistence granted',
                    options: {
                        variant: 'success',
                        autoHideduration: 3000,
                    },
                });
            }

            return granted;
        } catch (error) {
            console.error('Failed to request persistence:', error);
            throw error;
        }
    }

    get usagePercentage() {
        if (!this.estimate) return 0;
        if (this.estimate.quota === 0) return 0;
        return (this.estimate.usage / this.estimate.quota) * 100;
    }

    get formattedUsage() {
        if (!this.estimate) return 'Unknown';
        return this.formatBytes(this.estimate.usage);
    }

    get formattedQuota() {
        if (!this.estimate) return 'Unknown';
        return this.formatBytes(this.estimate.quota);
    }

    get isStorageLow() {
        return this.usagePercentage > 80;
    }

    get isStorageCritical() {
        return this.usagePercentage > 95;
    }

    private formatBytes(bytes: number) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    async clearCacheStorage() {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));

            // Refresh storage estimate
            await this.checkStorageStatus();

            if (this.rootStore.notificationStore) {
                this.rootStore.notificationStore.addNotification({
                    message: 'Cache cleared successfully',
                    options: {
                        variant: 'success',
                        autoHideduration: 3000,
                    },
                });
            }
        }
    }
}